import { useEffect, useState } from "react";
import { UserPlus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDelete } from "@/components/ConfirmDelete";

const ROLE_DESC = {
  admin: "Control total: usuarios, eventos y planes",
  usuario: "Crea y edita eventos y planes",
  observador: "Solo consulta mapas y planes",
};

const RoleSelect = ({ value, onChange, testid, disabled }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="w-40 h-9" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{Object.entries(ROLES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
  </Select>
);

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "", role: "usuario" });
  const [pw, setPw] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [del, setDel] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("Usuario creado");
      setOpen(false);
      setForm({ name: "", username: "", email: "", password: "", role: "usuario" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const patch = async (id, body, msg) => {
    try { await api.patch(`/users/${id}`, body); toast.success(msg); load(); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1100px]">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs tracking-[0.25em] text-sky-400 mb-2">ADMINISTRACIÓN</div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Usuarios y perfiles</h1>
        </div>
        <Button onClick={() => setOpen(true)} className="h-11" data-testid="create-user-button"><UserPlus className="w-4 h-4 mr-1" /> Nuevo usuario</Button>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        {Object.entries(ROLES).map(([k, v]) => (
          <div key={k} className="bg-[#111c38] border border-white/10 rounded-xl p-4">
            <div className="font-display font-semibold">{v}</div>
            <div className="text-xs text-slate-400 mt-1">{ROLE_DESC[k]}</div>
            <div className="font-mono text-[10px] text-amber-400 mt-2">{users.filter((u) => u.role === k).length} USUARIOS</div>
          </div>
        ))}
      </div>
      <div className="bg-[#111c38] border border-white/10 rounded-xl overflow-hidden" data-testid="users-admin-table">
        {users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center gap-4 px-5 py-3 border-b border-white/5" data-testid={`user-row-${u.id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm font-medium">{u.name} {u.id === me.id && <span className="text-[10px] text-sky-400 ml-1">(usted)</span>}</div>
              <div className="font-mono text-xs text-slate-500">{u.username ? `${u.username} · ` : ""}{u.email}</div>
            </div>
            <RoleSelect value={u.role} disabled={u.id === me.id} testid={`user-role-select-${u.id}`} onChange={(r) => patch(u.id, { role: r }, "Perfil actualizado")} />
            <Button size="sm" variant="ghost" onClick={() => { setPw(u); setNewPw(""); }} data-testid={`user-reset-password-${u.id}`} title="Cambiar contraseña"><KeyRound className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" disabled={u.id === me.id} className="hover:text-red-300" onClick={() => setDel(u)} data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111c38] border-white/10" data-testid="create-user-dialog">
          <DialogHeader><DialogTitle className="font-display">Nuevo usuario</DialogTitle><DialogDescription>Comunique al usuario su email y contraseña inicial.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {[["name", "Nombre / empleo", "text"], ["username", "Nombre de usuario (opcional, para entrar)", "text"], ["email", "Correo electrónico", "email"], ["password", "Contraseña (mín. 10)", "password"]].map(([k, l, t]) => (
              <div key={k} className="space-y-1"><Label className="text-xs text-slate-400">{l}</Label>
                <Input type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`new-user-${k}-input`} /></div>
            ))}
            <div className="space-y-1"><Label className="text-xs text-slate-400">Perfil</Label><RoleSelect value={form.role} onChange={(r) => setForm({ ...form, role: r })} testid="new-user-role-select" /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><Button onClick={create} data-testid="new-user-submit-button">Crear usuario</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pw} onOpenChange={(o) => !o && setPw(null)}>
        <DialogContent className="bg-[#111c38] border-white/10" data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle className="font-display">Nueva contraseña · {pw?.name}</DialogTitle></DialogHeader>
          <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Mínimo 10 caracteres" data-testid="reset-password-input" />
          <DialogFooter><Button onClick={async () => { if (await patch(pw.id, { password: newPw }, "Contraseña actualizada")) setPw(null); }} data-testid="reset-password-submit">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDelete open={!!del} title={`¿Eliminar a ${del?.name}?`} onCancel={() => setDel(null)}
        onConfirm={async () => { try { await api.delete(`/users/${del.id}`); toast.success("Usuario eliminado"); setDel(null); load(); } catch (e) { toast.error(errMsg(e)); } }} />
    </div>
  );
}
